import OneSchemaImporter, {
  OneSchemaImporterBaseProps,
} from '@oneschema/react';
import * as Sentry from '@sentry/react';
import { RefObject, useState } from 'react';
import { useSelector } from 'react-redux';
import { SystemRolesName } from 'src/api';
import { Divider, Link } from 'src/components/atoms';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import { EmptyState, Stepper } from 'src/components/molecules';
import { useGenerateJwtQuery, useMeQuery } from 'src/graphql';
import { FeatureFlag, State } from 'src/types';
import { env } from 'src/utils';
import {
  trackBulkReinviteFailed,
  trackBulkUploadStarted,
} from 'src/utils/analytics';
import * as Types from '../../../types';
import {
  Container,
  ContentContainer,
  StepperContainer,
} from './UsersUploadFile.styles';

export const UsersUploadFile = (
  props: Partial<OneSchemaImporterBaseProps> & {
    containerRef?: RefObject<HTMLDivElement | null>;
  },
) => {
  const [isOpen, setIsOpen] = useState(false);
  const { data: callingUser } = useMeQuery();
  const { data } = useGenerateJwtQuery();
  const organisation_id = useSelector(
    ({ organisation }: State) => organisation.organisation?.id,
  );
  const features = useSelector((state: State) => state.features);
  const segmentEnabled = isRequiredFeatureEnabled(
    features,
    FeatureFlag.FRONTEND_SEGMENT_TRACKING,
  );
  const userRoles = useSelector(
    (state: Types.State) =>
      state.globalUsers?.currentUser?.SystemRoles?.map((role) => role.name) ||
      [],
  );
  return (
    <>
      <Container ref={props.containerRef} data-testid="upload-users-page">
        <StepperContainer>
          <Stepper
            activeStep={0}
            steps={['File Upload', 'Activate Users', 'Deactivate Users']}
          />
          <Divider variant="horizontal" />
        </StepperContainer>
        <ContentContainer>
          <EmptyState
            data-testid="UploadCSV"
            loading={isOpen}
            buttonText="Upload CSV"
            description={
              <>
                Please ensure that your file is import-ready by referring to the{' '}
                <Link
                  size="medium"
                  target="_blank"
                  href="https://intercom.help/vault-platform-384e32b6cea0/en/articles/8260819-how-do-i-upload-users-using-a-csv-file"
                >
                  Vault FAQ{' '}
                </Link>
                section before uploading it.
              </>
            }
            onButtonClick={() => {
              setIsOpen(true);

              if (segmentEnabled && callingUser?.me) {
                trackBulkUploadStarted({
                  organisation_id: `${callingUser?.me.organisation}`,
                  is_admin: userRoles.includes(SystemRolesName.ADMIN),
                  is_super_admin: userRoles.includes(SystemRolesName.SUPER),
                });
              }
            }}
            title="Please upload your CSV"
          />
        </ContentContainer>
      </Container>
      {data?.generateJwt?.jwt && (
        <OneSchemaImporter
          /* managing state from your application */
          onRequestClose={() => {
            setIsOpen(false);
          }}
          /* optional config values */
          importConfig={{ type: 'local', metadataOnly: false }}
          devMode={process.env.NODE_ENV !== 'production'}
          className="oneschema-importer"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 2147483301,
          }}
          {...props}
          /* required config values */
          userJwt={data.generateJwt.jwt}
          saveSession={false}
          templateKey={'user_upload'}
          clientId={env.ONE_SCHEMA_CLIENT_ID}
          isOpen={isOpen}
          onSuccess={props.onSuccess}
          onError={(e) => {
            Sentry.captureMessage(e);
            if (segmentEnabled && organisation_id) {
              trackBulkReinviteFailed({ organisation_id });
            }
          }}
          onLaunched={({ error }) => {
            if (error) {
              Sentry.captureException(error);
              if (segmentEnabled && organisation_id) {
                trackBulkReinviteFailed({ organisation_id });
              }
            }
          }}
        />
      )}
    </>
  );
};
